import * as angular from 'angular';

import { IUserModel, PasswordValidation, ImportResultDto, IUserViewModel } from '../../models';
import { IRequestService, IUploadService } from '../../services';
import { ICurrentApplicationService } from './currentApplication.service';

export interface IAccountService {
    createOne: (user: IUserViewModel, createEmpty?: boolean) => angular.IPromise<IUserViewModel>;
    createEmpty: (userName?: string) => angular.IPromise<IUserModel>;
    updateOne: (user: IUserViewModel) => angular.IPromise<void>;
    updateForUser(userName: string, user: IUserModel): angular.IPromise<IUserModel>;
    updateForUserAsBody(userName: string, user): angular.IPromise<IUserViewModel>;
    updateInfosForUser(userName: string, user): angular.IPromise<void>;
    deleteOne: (userId: string) => angular.IPromise<boolean>;
    create: (users: IUserViewModel[]) => angular.IPromise<void>;
    resetPassword(userName: string): angular.IPromise<PasswordValidation[]>;
    changePassword(userName: string, newPassword: string): angular.IPromise<PasswordValidation[]>;
    updatePassword(userName: string, newPassword: string): angular.IPromise<PasswordValidation[]>;
    updatePasswordSafely(oldPassword: string, newPassword: string): angular.IPromise<PasswordValidation[]>;
    checkPassword(userName: string, password: string): angular.IPromise<boolean>;
    upload(file: File): angular.IPromise<ImportResultDto<IUserModel>>;
}

export class AccountService implements IAccountService {
    static serviceId = 'accountService';
    private _controllerUrl: string;

    static $inject = [
        '$q',
        'requestService',
        'uploadService',
        'currentApplicationService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _upload: IUploadService,
        private _currentApplicationService: ICurrentApplicationService
    ) {
    }

    private controllerUrl() {
        if (!this._controllerUrl) {
            this._controllerUrl = this._requestService.getUrlV1() + 'account/';
        }
        return this._controllerUrl;
    }

    create(users: IUserViewModel[]): angular.IPromise<void> {
        // TODO implement me
        return this._$q.reject('This method is not implemented');
    }

    createOne(user: IUserViewModel, createEmpty?: boolean): angular.IPromise<IUserViewModel> {
        let url = this.controllerUrl() + 'createOne/';
        if (createEmpty) {
            url += `${createEmpty}`;
        }
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'user/getAllWithRoles');
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'user');
        return this._requestService
            .post(url, user)
            .then(data => {
                this.cleanCache();
                return data;
            });
    }

    createEmpty(userName?: string): angular.IPromise<IUserModel> {
        let url = this.controllerUrl() + 'createEmpty/';
        if (userName) {
            url += userName;
        }
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'user/getAllWithRoles');
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'user');
        return this._requestService
            .post(url)
            .then(data => {
                this.cleanCache();
                return data;
            });
    }

    deleteOne(userId: string): angular.IPromise<boolean> {
        const url = this.controllerUrl() + 'deleteOne/' + userId;
        return this._requestService
            .delete(url)
            .then(data => {
                this.cleanCache();
                return data;
            });
    }

    updateOne(user: IUserViewModel): angular.IPromise<void> {
        const url = this.controllerUrl() + 'updateOne/';
        return this._requestService
            .post(url, user)
            .then(data => {
                this.cleanCache();
                return data;
            });
    }

    updateForUser(userName: string, user: IUserViewModel): angular.IPromise<IUserViewModel> {
        const url = this.controllerUrl() + userName + '/updateForUser';
        return this._requestService
            .post(url, user)
            .then(data => {
                this.cleanCache();
                return data;
            });
    }

    updateForUserAsBody(userName: string, user: IUserViewModel): angular.IPromise<IUserViewModel> {
        const url = this.controllerUrl() + 'updateForUserAsBody/';
        user.oldUserName = userName;
        return this._requestService
            .post(url, user)
            .then(data => {
                this.cleanCache();
                return data;
            });
    }

    updateInfosForUser(userName: string, user: IUserViewModel): angular.IPromise<void> {
        const url = this.controllerUrl() + userName + '/updateInfosForUser';
        return this._requestService
            .post(url, user)
            .then(data => {
                this.cleanCache();
                return data;
            });
    }

    resetPassword(userName: string): angular.IPromise<PasswordValidation[]> {
        const url = this.controllerUrl() + userName + '/resetPassword/';
        return this._requestService.post(url);
    }

    changePassword(userName: string, newPassword: string): angular.IPromise<PasswordValidation[]> {
        const url = this.controllerUrl() + userName + '/changePassword/';
        return this._requestService.post(url, newPassword);
    }

    updatePassword(userName: string, newPassword: string): angular.IPromise<PasswordValidation[]> {
        const url = this.controllerUrl() + userName + '/updatePassword/';
        return this._requestService.post(url, { password: newPassword });
    }

    updatePasswordSafely(oldPassword: string, newPassword: string): angular.IPromise<PasswordValidation[]> {
        const url = this.controllerUrl() + 'updatePassword';
        return this._requestService.post(url, { oldPassword, password: newPassword });
    }

    checkPassword(userName: string, password: string): angular.IPromise<boolean> {
        const url = this.controllerUrl() + userName + 'checkPassword/';
        const data = {
            password: password
        };
        return this._requestService.post(url, data);
    }

    upload(file: File): angular.IPromise<ImportResultDto<IUserModel>> {
        const url = this.controllerUrl() + 'importUsers';
        return this._upload.uploadFile<ImportResultDto<IUserModel>>(url, file);
    }

    private cleanCache() {
        const currentApplication = this._currentApplicationService.getApplicationConfiguration();
        if (currentApplication) {
            this._requestService.cleanCacheAtV1(`applicationUser/usersWithRoles/${currentApplication.guid}`);
        }
    }
}
